import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, Leaf, Calendar, ChevronDown, ChevronUp, Printer, RotateCcw } from 'lucide-react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from 'recharts';
import BreathingRoomAttribution from '../components/BreathingRoomAttribution';
import { getCopyForScore } from '../data/lifestyleDomainCopy';

const STORAGE_KEY = 'gw-lifestyle-assessment-progress-v1';

// Programs that have actual dedicated pages on the site (drives related-program links).
const VALID_PROGRAM_ROUTES = new Set([
  'fascia-reset',
  'back-reset',
  'crap-cleanse',
  'feel-good-detox',
  'terrain',
  'vip',
  'new-client',
]);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BOOKING_URL = "https://www.wellnessliving.com/rs/appointment-new/breathing_room-rws2se";

// Answer options with scores
const answerOptions = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 3, label: 'Sometimes' },
  { value: 5, label: 'Often' },
  { value: 8, label: 'Consistently' }
];

// All domains and questions
const domains = [
  {
    id: 'food-quality',
    title: 'Food Quality & Nutrient Density',
    icon: '🥗',
    questions: [
      { id: 'fq1', text: 'How often do you consume non-organic produce?', reverse: false },
      { id: 'fq2', text: 'How often do you consume conventionally raised meat or poultry?', reverse: false },
      { id: 'fq3', text: 'How often do you consume ultra-processed foods (boxed, packaged, frozen meals)?', reverse: false },
      { id: 'fq4', text: 'How often do you consume refined seed oils (canola, soybean, vegetable oil)?', reverse: false },
      { id: 'fq5', text: 'How often do you consume artificial sweeteners?', reverse: false },
      { id: 'fq6', text: 'How often do you consume refined white sugar or high-fructose products?', reverse: false },
      { id: 'fq7', text: 'How often do you drink unfiltered tap water?', reverse: false },
      { id: 'fq8', text: 'How often do you eat fewer than 2–3 servings of vegetables daily?', reverse: false },
    ]
  },
  {
    id: 'meal-rhythm',
    title: 'Meal Rhythm & Blood Sugar Stability',
    icon: '⏰',
    questions: [
      { id: 'mr1', text: 'How often do you skip meals?', reverse: false },
      { id: 'mr2', text: 'How often do you go more than 4–5 hours without eating?', reverse: false },
      { id: 'mr3', text: 'How often do you eat carbohydrate-heavy meals without protein or fat?', reverse: false },
      { id: 'mr4', text: 'How often do you crave sweets within two hours of eating?', reverse: false },
      { id: 'mr5', text: 'How often do you rely on caffeine to maintain energy?', reverse: false },
      { id: 'mr6', text: 'How often do you feel shaky, irritable, or foggy if you miss a meal?', reverse: false },
      { id: 'mr7', text: 'How often is your largest meal eaten late in the evening?', reverse: false },
      { id: 'mr8', text: 'How often do you snack due to stress rather than hunger?', reverse: false },
    ]
  },
  {
    id: 'digestive',
    title: 'Digestive Function',
    icon: '🌿',
    questions: [
      { id: 'df1', text: 'How often do you experience bloating?', reverse: false },
      { id: 'df2', text: 'How often do you experience constipation?', reverse: false },
      { id: 'df3', text: 'How often do you experience loose stools or urgency?', reverse: false },
      { id: 'df4', text: 'How often do you experience gas or belching after meals?', reverse: false },
      { id: 'df5', text: 'How often do you experience abdominal pain or cramping?', reverse: false },
      { id: 'df6', text: 'How often do you experience heartburn or indigestion?', reverse: false },
      { id: 'df7', text: 'How often do you notice undigested food in stool?', reverse: false },
      { id: 'df8', text: 'How often do you experience headaches or fatigue after eating?', reverse: false },
    ]
  },
  {
    id: 'detox',
    title: 'Detoxification & Toxic Load',
    subtitle: 'Internal Expression',
    icon: '💧',
    questions: [
      { id: 'dt1', text: 'How often do you experience unexplained fatigue or sluggishness?', reverse: false },
      { id: 'dt2', text: 'How often do you experience frequent headaches?', reverse: false },
      { id: 'dt3', text: 'How often do you experience skin reactions (rashes, itching, acne)?', reverse: false },
      { id: 'dt4', text: 'How often do you have dark circles or puffiness under the eyes?', reverse: false },
      { id: 'dt5', text: 'How often are you sensitive to smells, perfumes, or chemical fumes?', reverse: false },
      { id: 'dt6', text: 'How often do you feel mentally foggy or have poor concentration?', reverse: false },
      { id: 'dt7', text: 'How often do you retain cellulite or water despite exercise?', reverse: false },
      { id: 'dt8', text: 'How often do you feel worse in certain environments (offices, stores, hotels)?', reverse: false },
    ]
  },
  {
    id: 'environmental',
    title: 'Environmental & Chemical Exposure',
    subtitle: 'External Load',
    icon: '🏠',
    questions: [
      { id: 'ev1', text: 'How often do you use fragranced laundry detergents or fabric softeners?', reverse: false },
      { id: 'ev2', text: 'How often do you use scented candles, plug-ins, or air fresheners?', reverse: false },
      { id: 'ev3', text: 'How often do you use conventional cleaning products?', reverse: false },
      { id: 'ev4', text: 'How often do you store food in plastic containers?', reverse: false },
      { id: 'ev5', text: 'How often do you use non-stick cookware?', reverse: false },
      { id: 'ev6', text: 'How often do you use conventional cosmetics or personal care products?', reverse: false },
      { id: 'ev7', text: 'How often are you exposed to traffic fumes or industrial pollution?', reverse: false },
      { id: 'ev8', text: 'How often are you exposed to mold or damp environments?', reverse: false },
    ]
  },
  {
    id: 'circadian',
    title: 'Circadian Rhythm & Light Hygiene',
    icon: '🌙',
    questions: [
      { id: 'cr1', text: 'How often do you go to bed after 10:30 PM?', reverse: false },
      { id: 'cr2', text: 'How often do you wake between 1:00–4:00 AM?', reverse: false },
      { id: 'cr3', text: 'How often do you wake feeling unrefreshed?', reverse: false },
      { id: 'cr4', text: 'How often do you view screens within 30 minutes of sleep?', reverse: false },
      { id: 'cr5', text: 'How often do you check your phone immediately upon waking?', reverse: false },
      { id: 'cr6', text: 'How often do you get morning sunlight within 30 minutes of waking?', reverse: true },
      { id: 'cr7', text: 'How often do you feel a significant afternoon energy crash?', reverse: false },
      { id: 'cr8', text: 'How often do you sleep in a fully dark room?', reverse: true },
    ]
  },
  {
    id: 'stress',
    title: 'Stress & Emotional Load',
    icon: '💭',
    questions: [
      { id: 'st1', text: 'How often do you feel financially stressed?', reverse: false },
      { id: 'st2', text: 'How often do you feel relationship stress?', reverse: false },
      { id: 'st3', text: 'How often do you feel anxious without clear cause?', reverse: false },
      { id: 'st4', text: 'How often do you feel overwhelmed by daily demands?', reverse: false },
      { id: 'st5', text: 'How often do you consume news or social media that elevates stress?', reverse: false },
      { id: 'st6', text: 'How often do you feel reactive rather than responsive?', reverse: false },
      { id: 'st7', text: 'How often do you feel isolated or unsupported?', reverse: false },
      { id: 'st8', text: 'How often do you feel joy or lightness during the week?', reverse: true },
    ]
  },
  {
    id: 'nervous-system',
    title: 'Nervous System Regulation & Capacity',
    icon: '🧘',
    questions: [
      { id: 'ns1', text: 'How often do you feel "on edge" even when nothing is wrong?', reverse: false },
      { id: 'ns2', text: 'How often do you clench your jaw, shoulders, or abdomen?', reverse: false },
      { id: 'ns3', text: 'How often do you breathe through your mouth at rest?', reverse: false },
      { id: 'ns4', text: 'How often do you feel chronically rushed?', reverse: false },
      { id: 'ns5', text: 'How often do you struggle to relax without distraction?', reverse: false },
      { id: 'ns6', text: 'How often do you feel safe and grounded in your body?', reverse: true },
      { id: 'ns7', text: 'How often do you practice intentional stillness or mindfulness?', reverse: true },
      { id: 'ns8', text: 'How often do you rely on food, alcohol, scrolling, or stimulation to calm down?', reverse: false },
    ]
  }
];

// Calculate domain status
const getDomainStatus = (score, maxScore) => {
  const percentage = (score / maxScore) * 100;
  if (percentage <= 25) return { status: 'Resilient', color: 'emerald', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', borderColor: 'border-emerald-300' };
  if (percentage <= 50) return { status: 'Compensating', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-700', borderColor: 'border-amber-300' };
  return { status: 'Dysregulated', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700', borderColor: 'border-red-300' };
};

const LifestyleAssessmentPage = () => {
  const [stage, setStage] = useState('landing'); // landing, quiz, email, result
  const [currentDomain, setCurrentDomain] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedDomains, setExpandedDomains] = useState({});
  const [hasSavedProgress, setHasSavedProgress] = useState(false);

  // Load any saved progress on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.answers && Object.keys(data.answers).length > 0) {
          setHasSavedProgress(true);
        }
      }
    } catch (err) {
      console.warn('Could not read saved assessment progress:', err);
    }
  }, []);

  // Auto-save quiz progress to localStorage whenever answers/position change
  useEffect(() => {
    if (stage !== 'quiz') return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          answers,
          currentDomain,
          currentQuestion,
          savedAt: new Date().toISOString(),
        })
      );
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, [answers, currentDomain, currentQuestion, stage]);

  const resumeFromSaved = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      setAnswers(data.answers || {});
      setCurrentDomain(data.currentDomain || 0);
      setCurrentQuestion(data.currentQuestion || 0);
      setStage('quiz');
    } catch {
      /* ignore */
    }
  };

  const discardSaved = () => {
    localStorage.removeItem(STORAGE_KEY);
    setHasSavedProgress(false);
  };

  // Clear saved progress when assessment is completed
  useEffect(() => {
    if (stage === 'result') {
      localStorage.removeItem(STORAGE_KEY);
      // Auto-expand all domains so print captures everything
      const allOpen = {};
      domains.forEach((d) => { allOpen[d.id] = true; });
      setExpandedDomains(allOpen);
    }
  }, [stage]);

  const totalQuestions = domains.reduce((acc, d) => acc + d.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;
  const overallProgress = (answeredQuestions / totalQuestions) * 100;

  const currentDomainData = domains[currentDomain];
  const currentQuestionData = currentDomainData?.questions[currentQuestion];
  const currentQuestionId = currentQuestionData?.id;

  const calculateScore = (value, isReverse) => {
    if (isReverse) {
      // Reverse scoring: Never=8, Consistently=0
      const reverseMap = { 0: 8, 1: 5, 3: 3, 5: 1, 8: 0 };
      return reverseMap[value] ?? value;
    }
    return value;
  };

  const calculateDomainScores = () => {
    return domains.map(domain => {
      let score = 0;
      const maxScore = domain.questions.length * 8;
      
      domain.questions.forEach(q => {
        if (answers[q.id] !== undefined) {
          score += calculateScore(answers[q.id], q.reverse);
        }
      });
      
      return {
        ...domain,
        score,
        maxScore,
        ...getDomainStatus(score, maxScore)
      };
    });
  };

  const calculateOverallScores = (domainScores) => {
    const totalScore = domainScores.reduce((acc, d) => acc + d.score, 0);
    const totalMax = domainScores.reduce((acc, d) => acc + d.maxScore, 0);
    const loadPercentage = (totalScore / totalMax) * 100;
    
    // Resilience is inverse of load
    const resiliencePercentage = 100 - loadPercentage;
    
    return {
      lifestyleLoadIndex: Math.round(loadPercentage),
      resilienceCapacity: Math.round(resiliencePercentage),
      loadStatus: loadPercentage <= 30 ? 'Low' : loadPercentage <= 60 ? 'Moderate' : 'High',
      resilienceStatus: resiliencePercentage >= 70 ? 'Strong' : resiliencePercentage >= 40 ? 'Moderate' : 'Depleted'
    };
  };

  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestionId]: value });
  };

  const handleNext = () => {
    if (currentQuestion < currentDomainData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentDomain < domains.length - 1) {
      setCurrentDomain(currentDomain + 1);
      setCurrentQuestion(0);
    } else {
      setStage('email');
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentDomain > 0) {
      setCurrentDomain(currentDomain - 1);
      setCurrentQuestion(domains[currentDomain - 1].questions.length - 1);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const domainScores = calculateDomainScores();
    const overallScores = calculateOverallScores(domainScores);
    
    try {
      await fetch(`${BACKEND_URL}/api/quiz-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          quiz_type: 'lifestyle_assessment',
          result: JSON.stringify({ domainScores: domainScores.map(d => ({ id: d.id, score: d.score, status: d.status })), ...overallScores }),
          answers,
          created_at: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Error saving assessment result:', error);
    }
    
    setStage('result');
    setSubmitting(false);
  };

  const toggleDomainExpand = (domainId) => {
    setExpandedDomains(prev => ({ ...prev, [domainId]: !prev[domainId] }));
  };

  const domainScores = stage === 'result' ? calculateDomainScores() : [];
  const overallScores = stage === 'result' ? calculateOverallScores(domainScores) : {};

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="lifestyle-assessment-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6 md:py-8">
        
        {/* Back Link */}
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-4"
        >
          <ArrowLeft size={16} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        <AnimatePresence mode="wait">
          {/* LANDING PAGE */}
          {stage === 'landing' && (
            <motion.div
              key="landing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full font-body text-sm mb-6">
                <Leaf size={16} />
                SLOW WELLNESS Assessment
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl text-forest leading-tight mb-4">
                The Vitality Potential Tool
              </h1>
              <p className="text-stone-600 font-body text-lg mb-6 max-w-2xl mx-auto leading-relaxed">
                A grounded look at what may be quietly depleting your energy — and where your natural resilience already lives. No grades, no shame. Just a soft mirror for the body.
              </p>

              {/* Why it matters */}
              <div className="bg-cream/60 border border-stone-200 rounded-2xl p-5 mb-6 max-w-2xl mx-auto text-left">
                <p className="text-stone-600 font-body text-sm leading-relaxed">
                  Most of us are carrying more than we realize. Food, environment, sleep, breath, posture, stress, rhythm, and the state of our nervous system are all in constant conversation with the body. This tool gently maps where you're well-resourced and where one small shift could free up the most energy. Use it as a starting point — and a conversation opener with me when you're ready.
                </p>
              </div>

              {/* Resume Banner */}
              {hasSavedProgress && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 mb-6 max-w-2xl mx-auto text-left flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="resume-banner">
                  <div className="flex items-start gap-3">
                    <RotateCcw size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-medium text-amber-900">You have an assessment in progress</p>
                      <p className="text-amber-700 font-body text-xs">Your previous answers were saved on this device.</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={resumeFromSaved}
                      className="bg-amber-700 text-cream px-4 py-2 rounded-full font-body text-sm hover:bg-amber-800 transition-colors"
                      data-testid="resume-btn"
                    >
                      Resume
                    </button>
                    <button
                      onClick={discardSaved}
                      className="bg-white text-amber-700 border border-amber-300 px-4 py-2 rounded-full font-body text-sm hover:bg-amber-100 transition-colors"
                      data-testid="discard-btn"
                    >
                      Start fresh
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 text-left">
                <h3 className="font-heading text-xl text-forest mb-4">This assessment explores:</h3>
                
                <div className="grid md:grid-cols-2 gap-3 mb-6">
                  {domains.map((domain) => (
                    <div key={domain.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                      <span className="text-2xl">{domain.icon}</span>
                      <span className="font-body text-forest text-sm">{domain.title}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-terracotta/10 rounded-xl p-4 mb-6">
                  <p className="text-forest font-body font-medium">64 questions across 8 domains</p>
                  <p className="text-stone-600 font-body text-sm">Takes approximately 10–15 minutes · your progress is auto-saved so you can pause anytime</p>
                </div>

                <p className="text-stone-500 font-body text-sm italic">
                  Answer honestly based on your typical patterns over the past month. Enter your email at the end to receive your personalized results.
                </p>
              </div>

              <button
                onClick={() => setStage('quiz')}
                className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
              >
                Begin Assessment
                <ArrowRight size={18} />
              </button>
            </motion.div>
          )}

          {/* QUIZ QUESTIONS */}
          {stage === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Overall Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm font-body text-stone-500 mb-2">
                  <span>{answeredQuestions} of {totalQuestions} questions</span>
                  <span>{Math.round(overallProgress)}% complete</span>
                </div>
                <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-terracotta rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${overallProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              {/* Domain Header */}
              <div className="bg-forest text-cream rounded-xl p-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{currentDomainData.icon}</span>
                  <div className="min-w-0">
                    <p className="text-cream/70 text-[10px] uppercase tracking-wider">Domain {currentDomain + 1} of {domains.length}</p>
                    <h2 className="font-heading text-base sm:text-lg leading-tight">{currentDomainData.title}</h2>
                    {currentDomainData.subtitle && (
                      <p className="text-cream/70 text-xs">{currentDomainData.subtitle}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Question */}
              <div className="bg-white rounded-2xl p-5 md:p-6 shadow-md mb-4">
                <p className="text-stone-400 text-xs font-body mb-1.5">
                  Question {currentQuestion + 1} of {currentDomainData.questions.length}
                </p>
                <h3 className="font-heading text-base sm:text-lg text-forest mb-4 leading-snug">
                  {currentQuestionData.text}
                </h3>

                <div className="space-y-2">
                  {answerOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full text-left p-2.5 sm:p-3 rounded-lg border-2 transition-all ${
                        answers[currentQuestionId] === option.value
                          ? 'border-terracotta bg-terracotta/10'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          answers[currentQuestionId] === option.value
                            ? 'border-terracotta bg-terracotta'
                            : 'border-stone-300'
                        }`}>
                          {answers[currentQuestionId] === option.value && (
                            <CheckCircle size={12} className="text-white" />
                          )}
                        </div>
                        <span className="font-body text-sm text-forest">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between">
                <button
                  onClick={handlePrevious}
                  disabled={currentDomain === 0 && currentQuestion === 0}
                  className={`px-4 py-2.5 rounded-full font-body text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    currentDomain === 0 && currentQuestion === 0
                      ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
                      : 'bg-stone-200 text-forest hover:bg-stone-300'
                  }`}
                >
                  <ArrowLeft size={16} />
                  Previous
                </button>

                <button
                  onClick={handleNext}
                  disabled={answers[currentQuestionId] === undefined}
                  className={`px-4 py-2.5 rounded-full font-body text-sm font-medium transition-colors inline-flex items-center gap-2 ${
                    answers[currentQuestionId] === undefined
                      ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                      : 'bg-forest text-cream hover:bg-forest-dark'
                  }`}
                >
                  {currentDomain === domains.length - 1 && currentQuestion === currentDomainData.questions.length - 1 
                    ? 'See Results' 
                    : currentQuestion === currentDomainData.questions.length - 1 
                      ? 'Next Domain' 
                      : 'Next'}
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* EMAIL CAPTURE */}
          {stage === 'email' && (
            <motion.div
              key="email"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Mail size={32} className="text-emerald-600" />
                </div>
                
                <h2 className="font-heading text-2xl text-forest mb-4">
                  Assessment Complete!
                </h2>
                <p className="text-stone-600 font-body mb-8">
                  Enter your email to receive your personalized Lifestyle Load & Resilience report with detailed recommendations.
                </p>

                <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-stone-300 font-body text-forest focus:outline-none focus:ring-2 focus:ring-emerald-500/50 mb-4"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-forest text-cream px-6 py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : 'Get My Results'}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* RESULTS */}
          {stage === 'result' && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Overall Scores Header */}
              <div className="bg-gradient-to-br from-forest to-forest-dark text-cream rounded-2xl p-8 mb-8 print:bg-forest print:text-cream">
                <h1 className="font-heading text-3xl text-center mb-6">
                  Your Vitality Potential Results
                </h1>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Lifestyle Load Index */}
                  <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                    <p className="text-cream/70 text-sm uppercase tracking-wider mb-2">Lifestyle Load Index</p>
                    <p className="font-heading text-5xl text-terracotta mb-2">{overallScores.lifestyleLoadIndex}%</p>
                    <p className={`text-sm font-body ${
                      overallScores.loadStatus === 'Low' ? 'text-emerald-300' :
                      overallScores.loadStatus === 'Moderate' ? 'text-amber-300' : 'text-red-300'
                    }`}>
                      {overallScores.loadStatus} Load
                    </p>
                  </div>
                  
                  {/* Resilience Capacity */}
                  <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                    <p className="text-cream/70 text-sm uppercase tracking-wider mb-2">Resilience Capacity</p>
                    <p className="font-heading text-5xl text-emerald-300 mb-2">{overallScores.resilienceCapacity}%</p>
                    <p className={`text-sm font-body ${
                      overallScores.resilienceStatus === 'Strong' ? 'text-emerald-300' :
                      overallScores.resilienceStatus === 'Moderate' ? 'text-amber-300' : 'text-red-300'
                    }`}>
                      {overallScores.resilienceStatus} Resilience
                    </p>
                  </div>
                </div>
              </div>

              {/* Print button (hidden when printing) */}
              <div className="flex justify-end mb-4 print:hidden no-print">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 bg-white border border-stone-200 px-4 py-2 rounded-full font-body text-sm text-forest hover:bg-stone-50 transition-colors"
                  data-testid="print-results-btn"
                >
                  <Printer size={14} /> Print / Save as PDF
                </button>
              </div>

              {/* Radar Chart — at-a-glance domain view */}
              <div className="bg-white rounded-2xl shadow-lg p-6 mb-8" data-testid="results-radar">
                <h2 className="font-heading text-xl text-forest mb-1">At a Glance</h2>
                <p className="text-stone-500 font-body text-sm mb-4">Each axis is a domain. The further the line reaches outward, the more load is showing in that area.</p>
                <div className="w-full h-72 sm:h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      data={domainScores.map((d) => ({
                        domain: d.title.length > 18 ? `${d.title.slice(0, 16)}…` : d.title,
                        Load: Math.round((d.score / d.maxScore) * 100),
                      }))}
                      outerRadius="75%"
                    >
                      <PolarGrid stroke="#d6d3d1" />
                      <PolarAngleAxis
                        dataKey="domain"
                        tick={{ fill: '#4b4035', fontSize: 11, fontFamily: 'serif' }}
                      />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        tick={{ fill: '#a8a29e', fontSize: 10 }}
                      />
                      <Radar
                        name="Load"
                        dataKey="Load"
                        stroke="#b97a5a"
                        fill="#b97a5a"
                        fillOpacity={0.35}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Domain Breakdown */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
                <div className="p-6 border-b border-stone-200">
                  <h2 className="font-heading text-xl text-forest">Domain Breakdown</h2>
                  <p className="text-stone-500 font-body text-sm">Click each domain to see details</p>
                </div>
                
                <div className="divide-y divide-stone-100">
                  {domainScores.map((domain) => (
                    <div key={domain.id}>
                      <button
                        onClick={() => toggleDomainExpand(domain.id)}
                        className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{domain.icon}</span>
                          <div className="text-left">
                            <p className="font-body font-medium text-forest">{domain.title}</p>
                            {domain.subtitle && (
                              <p className="text-stone-400 text-xs">{domain.subtitle}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${domain.bgColor} ${domain.textColor}`}>
                            {domain.status}
                          </span>
                          {expandedDomains[domain.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {expandedDomains[domain.id] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4">
                              <div className={`${domain.bgColor} ${domain.borderColor} border rounded-xl p-4`}>
                                <div className="flex items-center justify-between mb-3">
                                  <span className="font-body text-sm text-stone-600">Domain Score</span>
                                  <span className={`font-heading text-lg ${domain.textColor}`}>
                                    {domain.score} / {domain.maxScore}
                                  </span>
                                </div>
                                <div className="h-2 bg-white rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${
                                      domain.status === 'Resilient' ? 'bg-emerald-500' :
                                      domain.status === 'Compensating' ? 'bg-amber-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${(domain.score / domain.maxScore) * 100}%` }}
                                  />
                                </div>
                                <p className="text-stone-600 font-body text-sm mt-3">
                                  {domain.status === 'Resilient' && 'This area shows good resilience. Keep supporting these habits.'}
                                  {domain.status === 'Compensating' && 'This area may benefit from some attention and gentle adjustments.'}
                                  {domain.status === 'Dysregulated' && 'This area is showing signs of strain. Consider prioritizing support here.'}
                                </p>
                              </div>

                              {/* Interpretation + Suggestions from Jill (loaded from /data/lifestyleDomainCopy.js) */}
                              {(() => {
                                const pct = Math.round((domain.score / domain.maxScore) * 100);
                                const copy = getCopyForScore(domain.id, pct);
                                if (!copy) return null;
                                const hasCopy = (copy.paragraph && copy.paragraph.trim()) || (copy.suggestions && copy.suggestions.length > 0);
                                return (
                                  <div className="mt-3 bg-white rounded-xl border border-stone-200 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-body mb-2">
                                      From Jill — {copy.level === 'low' ? 'Affirmation' : copy.level === 'medium' ? 'Gentle Next Steps' : 'Bigger Leverage Area'}
                                    </p>
                                    {hasCopy ? (
                                      <>
                                        {copy.paragraph && (
                                          <p className="font-body text-sm text-stone-700 leading-relaxed mb-3 whitespace-pre-line">
                                            {copy.paragraph}
                                          </p>
                                        )}
                                        {copy.suggestions && copy.suggestions.length > 0 && (
                                          <ul className="space-y-1.5">
                                            {copy.suggestions.map((s, idx) => (
                                              <li key={idx} className="flex items-start gap-2 font-body text-sm text-stone-600">
                                                <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-1.5 flex-shrink-0" />
                                                <span>{s}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                        {copy.relatedPrograms && copy.relatedPrograms.length > 0 && (
                                          <div className="mt-3 pt-3 border-t border-stone-100">
                                            <p className="text-xs font-body text-stone-400 mb-2">Related programs:</p>
                                            <div className="flex flex-wrap gap-2">
                                              {copy.relatedPrograms.map((p) => {
                                                const known = VALID_PROGRAM_ROUTES.has(p.id);
                                                return (
                                                  <Link
                                                    key={p.id}
                                                    to={known ? `/programs/${p.id}` : '/programs'}
                                                    title={p.why || ''}
                                                    className="inline-flex items-center gap-1 bg-forest/5 hover:bg-forest/10 text-forest text-xs font-body rounded-full px-3 py-1 transition-colors"
                                                  >
                                                    {p.title} <ArrowRight size={11} />
                                                  </Link>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <p className="font-body text-xs text-stone-400 italic">
                                        Personalized interpretation coming soon — Jill is adding this section in her own voice.
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>

              {/* Interpretation Guide */}
              <div className="bg-stone-100 rounded-2xl p-6 mb-8">
                <h3 className="font-heading text-lg text-forest mb-4">Understanding Your Results</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="font-body font-medium text-emerald-700">Resilient</span>
                    </div>
                    <p className="text-stone-600 font-body text-sm">
                      Your system is well-supported in this area. Maintain these positive patterns.
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="font-body font-medium text-amber-700">Compensating</span>
                    </div>
                    <p className="text-stone-600 font-body text-sm">
                      Your body is adapting but may benefit from some focused attention.
                    </p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="font-body font-medium text-red-700">Dysregulated</span>
                    </div>
                    <p className="text-stone-600 font-body text-sm">
                      This area is under significant stress and would benefit from prioritized support.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gradient-to-br from-terracotta/10 to-amber-50 rounded-2xl p-8 text-center">
                <h2 className="font-heading text-2xl text-forest mb-4">
                  Ready to Address Your Lifestyle Load?
                </h2>
                <p className="text-stone-600 font-body mb-6 max-w-xl mx-auto">
                  An assessment reveals patterns. A personalized session creates a path forward.
                  Let's explore what your body is truly asking for.
                </p>

                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-forest text-cream px-8 py-4 rounded-full font-body font-medium hover:bg-forest-dark transition-colors inline-flex items-center gap-2"
                >
                  <Calendar size={18} />
                  Book a Wellness Consultation
                </a>
                <div className="mt-5 flex justify-center">
                  <BreathingRoomAttribution align="center" />
                </div>
              </div>

              {/* Retake */}
              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    setStage('landing');
                    setCurrentDomain(0);
                    setCurrentQuestion(0);
                    setAnswers({});
                    setEmail('');
                    setExpandedDomains({});
                  }}
                  className="text-stone-500 font-body text-sm hover:text-forest transition-colors"
                >
                  Retake Assessment
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LifestyleAssessmentPage;
